import { useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../../Services/ApiClient';
import { decode } from '../../Services/DecodeToken';
import useGetCustomData from '../../Hooks/useGetCustomData';
import { USER_ROLE } from '../../Types';
import { CompanySelect } from '../../Components/Select/CompanySelect';
import LocationChecklist from '../../Components/Checkbox/LocationCheckList';
import {
  Form,
  FormSection,
  SectionTitle,
  SectionDescription,
  FormGrid,
  FormTextField,
  FormCheckbox,
  FormFileUpload,
  FormRow,
} from '../../Components/Form';
import { ImageIcon } from 'lucide-react';

const normalizeBitFlag = (value: unknown): number => {
  if (value && typeof value === 'object' && 'data' in value && Array.isArray((value as { data: number[] }).data)) {
    return (value as { data: number[] }).data[0] ?? 0;
  }
  return Number(value) || 0;
};

const parseLocationIds = (raw: unknown): number[] => {
  if (!Array.isArray(raw)) return [];
  return raw.map(Number).filter((n) => Number.isInteger(n) && n > 0);
};

export const NonAbatixCategoryForm: React.FC<any> = ({ onSubmit, onCancel, defaultValues }) => {
  const token: any = decode();
  const [CID] = useGetCustomData('/api/company/userregister');

  const [selectedCompany, setSelectedCompany] = useState<string>(
    defaultValues?.customer_id ? String(defaultValues.customer_id) : ''
  );

  const companyIdForLocations = selectedCompany || (defaultValues?.customer_id ? String(defaultValues.customer_id) : '');
  const [locations, loadingLocations] = useGetCustomData(
    companyIdForLocations ? `/api/location/${companyIdForLocations}` : ''
  );

  const activeLocations = useMemo(() => {
    if (!Array.isArray(locations)) return [];
    return [...locations]
      .filter((loc: any) => Number(loc.IsActive) === 1)
      .sort((a: any, b: any) => String(a.name || '').localeCompare(String(b.name || ''), undefined, { sensitivity: 'base' }));
  }, [locations]);

  const checklistLocations = useMemo(
    () =>
      activeLocations.map((loc: any) => ({
        OVID: Number(loc.OVID),
        name: loc.name || '',
        CID: String(loc.CID ?? loc.CustomerId ?? companyIdForLocations ?? ''),
        CustomerName: loc.CustomerName || loc.customer_name || '',
        CustomerId: String(loc.CustomerId ?? loc.CID ?? companyIdForLocations ?? ''),
        ShipToId: loc.ShipToId || '',
      })),
    [activeLocations, companyIdForLocations]
  );

  const [formData, setFormData] = useState<any>({
    category_name: defaultValues?.category_name || '',
    category_code: defaultValues?.category_code || '',
    image: null,
    active_flag: defaultValues?.active_flag !== undefined ? normalizeBitFlag(defaultValues.active_flag) : 1,
    customer_id: defaultValues?.customer_id || '',
    location_ids: parseLocationIds(defaultValues?.location_ids),
  });

  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const initializedCompanyRef = useRef<string>(defaultValues ? companyIdForLocations : '');

  useEffect(() => {
    if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && CID.length > 0 && !selectedCompany) {
      const defaultCID = CID[0].CID;
      setSelectedCompany(defaultCID);
      setFormData((prev: any) => ({ ...prev, customer_id: defaultCID }));
    }
  }, [token.roles.id, CID, selectedCompany]);

  useEffect(() => {
    const fetchImage = async () => {
      if (!defaultValues?.category_id || imagePreview) return;
      try {
        const res = await apiClient(`/api/non-abatix/categories/image/${defaultValues.category_id}`);
        if (!res.ok) return;
        const blob = await res.blob();
        setImagePreview(URL.createObjectURL(blob));
      } catch (err) {
        console.error('Image fetch error:', err);
      }
    };
    fetchImage();
  }, [defaultValues?.category_id, imagePreview]);

  useEffect(() => {
    if (defaultValues) return;
    if (!selectedCompany) {
      initializedCompanyRef.current = '';
      return;
    }
    if (loadingLocations) return;
    if (!activeLocations.length) {
      initializedCompanyRef.current = selectedCompany;
      return;
    }
    if (initializedCompanyRef.current === selectedCompany) return;
    initializedCompanyRef.current = selectedCompany;
    setFormData((prev: any) => ({
      ...prev,
      location_ids: activeLocations.map((loc: any) => Number(loc.OVID)),
    }));
  }, [selectedCompany, activeLocations, defaultValues, loadingLocations]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData((prev: any) => ({ ...prev, [name]: checked ? 1 : 0 }));
    } else {
      setFormData((prev: any) => ({ ...prev, [name]: value }));
    }
  };

  const handleLocationChecklistChange = (e: { target: { name: string; value: number[] } }) => {
    const values = Array.isArray(e.target.value) ? e.target.value.map(Number).filter((n) => n > 0) : [];
    setFormData((prev: any) => ({ ...prev, location_ids: values }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const visibleIds = new Set(activeLocations.map((loc: any) => Number(loc.OVID)));
    const hiddenAssignedIds = parseLocationIds(defaultValues?.location_ids).filter((id) => !visibleIds.has(id));
    const mergedIds = [...(formData.location_ids || []), ...hiddenAssignedIds];
    await onSubmit({
      ...formData,
      location_ids: mergedIds.filter((id, index) => mergedIds.indexOf(id) === index),
    });
    onCancel();
  };

  const handleRemoveImage = () => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview);
    setImagePreview(null);
    setFormData((prev: any) => ({ ...prev, image: null }));
  };

  const selectedCount = (formData.location_ids || []).filter((id: number) =>
    activeLocations.some((loc: any) => Number(loc.OVID) === id)
  ).length;
  const hasCompany = !!formData.customer_id;
  const hasLocations = activeLocations.length > 0;
  const disableSubmit = !hasCompany || loadingLocations || (hasLocations && selectedCount === 0);

  return (
    <Form
      embedded
      hideTitle
      onSubmit={handleSubmit}
      title={defaultValues ? 'Edit Category' : 'Add Category'}
      subtitle="Create or update a product category for Non-Abatix inventory."
      submitLabel="Save Category"
      onCancel={onCancel}
      disableSubmit={disableSubmit}
    >
      {!defaultValues && (
        <FormSection>
          <SectionTitle>Company</SectionTitle>
          <SectionDescription>Select the company this category belongs to.</SectionDescription>
          <FormRow>
            <CompanySelect
              companies={CID}
              selectedCompany={selectedCompany}
              defaultName="Select Company"
              onChange={(newCompany: any) => {
                setSelectedCompany(newCompany);
                setFormData((prev: any) => ({ ...prev, customer_id: newCompany, location_ids: [] }));
              }}
            />
          </FormRow>
        </FormSection>
      )}

      <FormSection>
        <SectionTitle>Locations</SectionTitle>
        <SectionDescription>
          Search and choose which locations should show this category. Unchecked locations will hide it.
        </SectionDescription>
        {!hasCompany ? (
          <SectionDescription>Select a company to load its locations.</SectionDescription>
        ) : (
          <LocationChecklist
            name="location_ids"
            selectedValues={formData.location_ids || []}
            onChange={handleLocationChecklistChange}
            ovs={checklistLocations}
            loadingOvs={loadingLocations}
            disabled={loadingLocations}
            emptyMessage="This account has no active locations."
          />
        )}
      </FormSection>

      <FormSection>
        <SectionTitle>Category Details</SectionTitle>
        <SectionDescription>Enter the category name and optional code.</SectionDescription>
        <FormGrid $columns={1}>
          <FormTextField
            label="Category Name"
            name="category_name"
            value={formData.category_name || ''}
            onChange={handleChange}
            required
          />
          <FormTextField
            label="Category Code"
            name="category_code"
            value={formData.category_code || ''}
            onChange={handleChange}
          />
        </FormGrid>
      </FormSection>

      <FormSection>
        <SectionTitle>Image</SectionTitle>
        <SectionDescription>Optional image for mobile catalog display.</SectionDescription>
        <FormFileUpload
          accept="image/*"
          fileName={formData.image?.name}
          placeholder="Click to upload an image"
          subtext="PNG or JPG recommended"
          previewUrl={imagePreview}
          icon={<ImageIcon size={28} />}
          onChange={(file) => {
            if (file) {
              setFormData((prev: any) => ({ ...prev, image: file }));
              setImagePreview(URL.createObjectURL(file));
            }
          }}
          onRemove={handleRemoveImage}
        />
      </FormSection>

      <FormSection>
        <FormCheckbox
          name="active_flag"
          label="Active Status"
          checked={formData.active_flag === 1}
          onChange={handleChange}
        />
      </FormSection>
    </Form>
  );
};
